import { TOOLBAR_BUTTON_SX } from "@/controls/buttons/toolbarButton";
import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, MenuItem, TextField, Tooltip } from "@mui/material";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { StandSelector } from "@/shared/selectors/StandSelector";
import type {
	BarrelOption,
	MillSizeOption,
} from "../repositories/RollshopOperationsRepository";

const STATUS_OPTIONS: { value: string; label: string }[] = [
	{ value: "AVAILABLE", label: "Available" },
	{ value: "PREPAREDFORLINE", label: "Prepared for Line" },
	{ value: "INUSE", label: "In Use" },
	{ value: "ASSEMBLE", label: "Assemble" },
	{ value: "PENDINGCHECK", label: "Pending Check" },
];

interface Props {
	search: string;
	onSearchChange: (v: string) => void;
	statusCode: string | null;
	onStatusChange: (v: string | null) => void;
	millSizeId: number | null;
	onMillSizeChange: (v: number | null) => void;
	standId: number | null;
	onStandChange: (v: number | null) => void;
	barrelId: number | null;
	onBarrelChange: (v: number | null) => void;
	millSizes: MillSizeOption[];
	barrels: BarrelOption[];
	onExportExcel: () => void;
	onAdd: () => void;
	addDisabled?: boolean;
	addDisabledReason?: string;
	onReset: () => void;
	canReset: boolean;
}

export const RollsetsToolbar = ({
	search,
	onSearchChange,
	statusCode,
	onStatusChange,
	millSizeId,
	onMillSizeChange,
	standId,
	onStandChange,
	barrelId,
	onBarrelChange,
	millSizes,
	barrels,
	onExportExcel,
	onAdd,
	addDisabled = false,
	addDisabledReason,
	onReset,
	canReset,
}: Props) => {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ width: 220 }}>
				<TextField
					size="small"
					label="Search"
					placeholder="Name / Top / Bottom Roll / Barrel"
					fullWidth
					value={search}
					onChange={(e) => onSearchChange(e.target.value)}
				/>
			</Box>
			<Box sx={{ width: 170 }}>
				<TextField
					select
					size="small"
					label="Op. Status"
					fullWidth
					value={statusCode ?? ""}
					onChange={(e) =>
						onStatusChange(e.target.value === "" ? null : e.target.value)
					}
				>
					<MenuItem value="">All</MenuItem>
					{STATUS_OPTIONS.map((s) => (
						<MenuItem key={s.value} value={s.value}>
							{s.label}
						</MenuItem>
					))}
				</TextField>
			</Box>
			<Box sx={{ width: 130 }}>
				<TextField
					select
					size="small"
					label="Mill Size"
					fullWidth
					value={millSizeId == null ? "" : String(millSizeId)}
					onChange={(e) =>
						onMillSizeChange(
							e.target.value === "" ? null : Number(e.target.value),
						)
					}
				>
					<MenuItem value="">All</MenuItem>
					{millSizes.map((ms) => (
						<MenuItem key={ms.Id} value={String(ms.Id)}>
							{ms.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>
			<Box sx={{ width: 130 }}>
				<StandSelector
					includeStraighteners
					includeAll
					fullWidth
					value={standId}
					onChange={onStandChange}
				/>
			</Box>
			<Box sx={{ width: 150 }}>
				<TextField
					select
					size="small"
					label="Barrel"
					fullWidth
					value={barrelId == null ? "" : String(barrelId)}
					onChange={(e) =>
						onBarrelChange(
							e.target.value === "" ? null : Number(e.target.value),
						)
					}
				>
					<MenuItem value="">All</MenuItem>
					{barrels.map((b) => (
						<MenuItem key={b.Id} value={String(b.Id)}>
							{b.Name}
						</MenuItem>
					))}
				</TextField>
			</Box>
			<ResetFiltersButton onClick={onReset} disabled={!canReset} />

			<Tooltip title={addDisabled ? addDisabledReason : "Add new rollset"}>
				<span>
					<Button
						size="medium"
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={onAdd}
						disabled={addDisabled}
						sx={TOOLBAR_BUTTON_SX}
					>
						ADD
					</Button>
				</span>
			</Tooltip>

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			<Box sx={{ width: 200 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
			</Box>
		</Box>
	);
};
